import {
  resolveMyBrandBqKeys,
  type CompTopicDetailBucket,
  type OverviewCompetitorChartRow,
  type OverviewCompetitorReportRow,
} from "@nexus/contracts";
import type { CompetitorFiltersInput } from "@/lib/queries/dashboard.queries";
import { config } from "@/config";
import { formatBrandName } from "@/features/dashboard/lib/format.js";
import { buildTopicDetailSearchFromReportRows } from "./buildTopicDetailSearch.js";
import { resolveTopicDetailSegment2 } from "./resolveTopicDetailSegment2.js";
import {
  TOPIC_DETAIL_SEGMENT1,
  TOPIC_DETAIL_SEGMENT3,
} from "./topicDetailContext.js";

/** Drawer + comp topic-detail API context for a clicked comparison-chart bar. */
export interface CompTopicDetailContext {
  topicTitle: string;
  topicDescription: string;
  bucket: CompTopicDetailBucket;
  entityDisplayLabel: string;
  featureTimeFrame: string;
  scenario: string;
  /** Reco `search` param — AND-joined combinations for Examples/Fatigue BQ. */
  search: string;
  segment1: typeof TOPIC_DETAIL_SEGMENT1;
  segment2: string;
  segment3: typeof TOPIC_DETAIL_SEGMENT3;
  /** CSV passed to BQ `competitor` for the clicked bucket. */
  competitorBqFilter: string;
  chartMetrics: {
    Days: number;
    Assets: number;
    assetPct: number;
    /** Winsorized-normalized Days/Assets in [-1, 1] (matches Retool's chart value); null ⇒ no baseline. */
    daysAssets: number | null;
  };
}

export type OverviewTranslate = (key: string, defaultValue: string) => string;

export interface OverviewSeriesLabels {
  you: string;
  mainCompetitor: string;
  benchmark: string;
}

export interface ResolveCompTopicDetailContextOptions {
  reportRows: OverviewCompetitorReportRow[];
  chartRows: OverviewCompetitorChartRow[];
  topicTitle: string;
  bucket: CompTopicDetailBucket;
  competitorFilters: CompetitorFiltersInput;
  defaultBrand: string | null | undefined;
  mainCompetitorLabel: string | null;
  t: OverviewTranslate;
}

function pickPrimaryReportRow(
  rows: OverviewCompetitorReportRow[],
): OverviewCompetitorReportRow | null {
  if (rows.length === 0) return null;
  return rows.reduce((best, row) => (row.Assets > best.Assets ? row : best), rows[0]!);
}

export function resolveCompetitorBqFilter(
  bucket: CompTopicDetailBucket,
  filters: {
    myBrand: string | null;
    defaultBrand: string | null | undefined;
    mainCompetitor: string | null;
    benchmark: string[];
  },
): string | null {
  if (bucket === "you") {
    const keys = resolveMyBrandBqKeys(filters.myBrand, filters.defaultBrand);
    return keys.length > 0 ? keys.join(",") : null;
  }
  if (bucket === "mainCompetitor") {
    const name = filters.mainCompetitor?.trim();
    return name ? name : null;
  }
  const joined = filters.benchmark.filter(Boolean).join(",");
  return joined || null;
}

export function resolveOverviewSeriesLabels(options: {
  myBrand: string | null;
  defaultBrand: string | null | undefined;
  mainCompetitor: string | null;
  t: OverviewTranslate;
}): OverviewSeriesLabels {
  const youName = options.myBrand?.trim() || options.defaultBrand?.split(",")[0]?.trim() || "";
  const competitorName = options.mainCompetitor?.trim() ?? "";
  return {
    you: youName
      ? formatBrandName(youName)
      : options.t("overview.legend.myBrand", "My Brand"),
    mainCompetitor: competitorName
      ? formatBrandName(competitorName)
      : options.t("overview.legend.mainCompetitor", "Main Competitor"),
    benchmark: options.t("overview.legend.benchmark", "Benchmark"),
  };
}

function resolveEntityDisplayLabel(
  bucket: CompTopicDetailBucket,
  labels: OverviewSeriesLabels,
): string {
  return labels[bucket];
}

/**
 * Build comp topic-detail context from chart row (topic + bucket) and report rows
 * (topic-level metadata). Bucket scoping is enforced via `competitorBqFilter` at query time.
 */
export function resolveCompTopicDetailContext(
  options: ResolveCompTopicDetailContextOptions,
): CompTopicDetailContext | null {
  const {
    reportRows,
    chartRows,
    topicTitle,
    bucket,
    competitorFilters,
    defaultBrand,
    mainCompetitorLabel,
    t,
  } = options;

  const chartRow = chartRows.find(
    (row) => row.Topic_Title === topicTitle && row.bucket === bucket,
  );
  if (!chartRow) return null;

  const competitorBqFilter = resolveCompetitorBqFilter(bucket, {
    myBrand: competitorFilters.myBrand,
    defaultBrand,
    mainCompetitor: competitorFilters.mainCompetitor,
    benchmark: competitorFilters.benchmark,
  });
  if (!competitorBqFilter) return null;

  const matchingRows = reportRows.filter((row) => row.Topic_Title === topicTitle);
  const primary = pickPrimaryReportRow(matchingRows);
  if (!primary) return null;

  if (config.isDev && matchingRows.length > 1) {
    console.warn(
      "[comp-topic-detail] Multiple report rows for topic — using highest-Assets row for scenario/segment; OR-joining all combinations for search",
      { topicTitle, rowCount: matchingRows.length, selectedAssets: primary.Assets },
    );
  }

  return {
    topicTitle,
    topicDescription: primary.Topic_Description,
    bucket,
    entityDisplayLabel: resolveEntityDisplayLabel(
      bucket,
      resolveOverviewSeriesLabels({
        myBrand: competitorFilters.myBrand,
        defaultBrand,
        mainCompetitor: mainCompetitorLabel,
        t,
      }),
    ),
    featureTimeFrame: primary.feature_time_frame,
    scenario: primary.Scenario,
    search: buildTopicDetailSearchFromReportRows(matchingRows),
    segment1: TOPIC_DETAIL_SEGMENT1,
    segment2: resolveTopicDetailSegment2(primary.Scenario, primary.feature_time_frame),
    segment3: TOPIC_DETAIL_SEGMENT3,
    competitorBqFilter,
    chartMetrics: {
      Days: chartRow.Days,
      Assets: chartRow.Assets,
      assetPct: chartRow.assetPct,
      daysAssets: chartRow.daysAssets,
    },
  };
}

/** Comp drawer uses 960px on all tabs (Figma split layout). */
export const COMP_TOPIC_DETAIL_DRAWER_WIDTH = "sm:max-w-[960px]";
